import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  Search,
  Pin,
  PinOff,
  ArrowRight,
  ShieldCheck,
  Lock,
  AppWindow,
  CheckCircle2,
} from 'lucide-react';
import MainLayout from '../components/MainLayout';
import { useApplications, APP_ICON_MAP, APP_COLOR_MAP } from '../hooks/useApplications';
import { useAuth } from '../contexts/AuthContext';
import appSecurityService from '../security/AppSecurityService';

export default function AppsCatalog() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { applications, pinnedApplications, updatePinned, loading } = useApplications();
  const [searchQuery, setSearchQuery] = useState('');
  const [pinning, setPinning] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [, setSecTick] = useState(0);

  React.useEffect(() => {
    const handleSecUpdate = () => setSecTick((t) => t + 1);
    window.addEventListener('app-security-updated', handleSecUpdate);
    return () => window.removeEventListener('app-security-updated', handleSecUpdate);
  }, []);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3000);
  };

  const handleTogglePin = async (app) => {
    const isCurrentlyPinned = pinnedApplications.some((p) => p.slug === app.slug);
    let nextPinnedSlugs = [];

    if (isCurrentlyPinned) {
      nextPinnedSlugs = pinnedApplications.filter((p) => p.slug !== app.slug).map((p) => p.slug);
    } else {
      if (pinnedApplications.length >= 3) {
        showToast('Maximum of 3 pinned applications reached. Unpin an app first.');
        return;
      }
      nextPinnedSlugs = [...pinnedApplications.map((p) => p.slug), app.slug];
    }

    setPinning(true);
    try {
      await updatePinned(nextPinnedSlugs);
      showToast(isCurrentlyPinned ? `Unpinned ${app.name}` : `Pinned ${app.name} to header`);
    } catch (err) {
      showToast('Failed to update pinned applications');
    } finally {
      setPinning(false);
    }
  };

  const filteredApps = applications.filter((app) => {
    if (!app.enabled) return false;
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      app.name.toLowerCase().includes(q) ||
      (app.description && app.description.toLowerCase().includes(q)) ||
      (app.category && app.category.toLowerCase().includes(q)) ||
      app.slug.toLowerCase().includes(q)
    );
  });

  // Group by category
  const categories = [...new Set(filteredApps.map((a) => a.category || 'General'))];

  return (
    <MainLayout>
      {toastMsg && (
        <div className="admin-toast admin-toast-success animate-fade-in" style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999 }}>
          <CheckCircle2 size={16} />
          <span>{toastMsg}</span>
        </div>
      )}

      {/* Page Header - Full Bleed Edge-to-Edge */}
      <div className="apps-catalog-header notes-hero-banner">
        <div className="notes-hero-content">
          <div className="apps-catalog-header-copy">
            <div className="security-badge-top notes-hero-badge">
              <LayoutGrid size={14} /> Application Ecosystem
            </div>
            <h1 className="apps-catalog-title notes-hero-title">Applications & Suites</h1>
            <p className="apps-catalog-subtitle notes-hero-subtitle">
              Browse encrypted modules, quick launch tools, and pin up to 3 applications for rapid access.
            </p>
          </div>

          <div className="apps-search-box" style={{ background: 'rgba(255, 255, 255, 0.15)', border: '1px solid rgba(255, 255, 255, 0.35)', borderRadius: '10px', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={17} color="#ffffff" />
            <input
              type="text"
              className="apps-search-input"
              placeholder="Search applications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ background: 'transparent', border: 'none', color: '#ffffff', outline: 'none' }}
            />
          </div>
        </div>
      </div>

      <div className="apps-catalog-container">

        {/* Pinned Quick Bar */}
        <div className="pinned-bar-card glass-panel">
          <div className="pinned-bar-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Pin size={16} color="#0d9488" />
              <strong>Pinned Header Applications ({pinnedApplications.length}/3)</strong>
            </div>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Pinned items appear directly in your top navigation bar
            </span>
          </div>

          <div className="pinned-bar-chips">
            {pinnedApplications.length === 0 ? (
              <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                No applications pinned yet. Click the pin icon on any app card below.
              </span>
            ) : (
              pinnedApplications.map((app) => {
                const IconComponent = APP_ICON_MAP[app.slug] || APP_ICON_MAP[app.icon] || AppWindow;
                const brandColor = APP_COLOR_MAP[app.slug] || '#3b82f6';
                return (
                  <div key={app.id || app.slug} className="pinned-chip-card">
                    <div className="chip-icon-box" style={{ background: `${brandColor}18`, color: brandColor }}>
                      <IconComponent size={16} />
                    </div>
                    <span className="chip-title">{app.name}</span>
                    <button
                      type="button"
                      className="chip-unpin-btn"
                      onClick={() => handleTogglePin(app)}
                      title={`Unpin ${app.name}`}
                      disabled={pinning}
                    >
                      <PinOff size={13} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Categorized Apps Grid */}
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Loading applications catalog...
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="glass-panel" style={{ padding: '60px', textAlign: 'center', borderRadius: '14px' }}>
            <AppWindow size={40} color="var(--text-secondary)" style={{ margin: '0 auto 12px', opacity: 0.6 }} />
            <h3>No applications found</h3>
            <p style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
              Try adjusting your search query or check your account permissions.
            </p>
          </div>
        ) : (
          categories.map((cat) => {
            const appsInCategory = filteredApps.filter((a) => (a.category || 'General') === cat);
            return (
              <div key={cat} className="apps-category-section">
                <h3 className="apps-category-heading">{cat}</h3>
                <div className="apps-catalog-grid">
                  {appsInCategory.map((app) => {
                    const IconComponent = APP_ICON_MAP[app.slug] || APP_ICON_MAP[app.icon] || AppWindow;
                    const brandColor = APP_COLOR_MAP[app.slug] || '#3b82f6';
                    const isPinned = pinnedApplications.some((p) => p.slug === app.slug);

                    return (
                      <div key={app.id || app.slug} className="app-catalog-card glass-panel">
                        <div className="app-card-top">
                          <div
                            className="app-card-icon"
                            style={{
                              background: `${brandColor}18`,
                              color: brandColor,
                              borderColor: `${brandColor}30`,
                            }}
                          >
                            <IconComponent size={24} />
                          </div>
                          <div className="app-card-badges">
                            {appSecurityService.isAppProtected(user?.id, app.slug) && (
                              <span className="app-badge-lock" style={{ background: 'rgba(16, 185, 129, 0.14)', color: '#10b981', borderColor: 'rgba(16, 185, 129, 0.3)' }} title="Protected with Per-App Lock">
                                <ShieldCheck size={12} /> Protected
                              </span>
                            )}
                            <button
                              type="button"
                              className={`app-pin-btn ${isPinned ? 'pinned' : ''}`}
                              onClick={() => handleTogglePin(app)}
                              title={isPinned ? 'Unpin from header' : 'Pin to header'}
                              disabled={pinning}
                            >
                              <Pin size={15} />
                            </button>
                          </div>
                        </div>

                        <div className="app-card-body">
                          <h4 className="app-card-title">{app.name}</h4>
                          <p className="app-card-desc">{app.description}</p>
                        </div>

                        <div className="app-card-footer">
                          <div className="app-perms-chips">
                            <span className="perm-chip">Access</span>
                            {app.permissions?.create && <span className="perm-chip">Create</span>}
                            {app.permissions?.reveal && <span className="perm-chip">Reveal</span>}
                          </div>
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => navigate(`/apps/${app.slug}`)}
                            title={`Launch ${app.name}`}
                          >
                            <span>Open</span>
                            <ArrowRight size={13} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </MainLayout>
  );
}
